import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { fmtMXN, fmtDate } from "@/lib/api";
import { Building2, ShieldCheck, ShieldAlert, BadgeDollarSign, Receipt, Clock, AlertTriangle, Sparkles } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, tone = "default" }) {
  const tones = {
    default: "text-foreground",
    primary: "text-primary",
    danger: "text-red-500",
    success: "text-emerald-500",
  };
  return (
    <Card data-testid={`stat-${label}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`mt-2 text-3xl font-black ${tones[tone]}`}>{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
          </div>
          <div className="rounded-lg bg-secondary p-2.5">
            <Icon className="h-5 w-5 text-foreground/70" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="text-muted-foreground">Cargando…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-black tracking-tight">Panel Global</h2>
        <p className="text-sm text-muted-foreground mt-1">Vista general del licenciamiento y operación.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Building2} label="Clientes activos" value={data.clientes_activos} tone="primary" />
        <Stat icon={ShieldAlert} label="Clientes suspendidos" value={data.clientes_suspendidos} />
        <Stat icon={ShieldCheck} label="Sucursales activas" value={data.sucursales_activas} tone="success" />
        <Stat icon={ShieldAlert} label="Sucursales suspendidas" value={data.sucursales_suspendidas} />
        <Stat icon={BadgeDollarSign} label="Ventas del día" value={fmtMXN(data.ventas_dia)} sub={`${data.ventas_dia_count} órdenes cobradas`} tone="primary" />
        <Stat icon={Receipt} label="Ventas del mes" value={fmtMXN(data.ventas_mes)} />
        <Stat icon={Sparkles} label="En periodo de prueba" value={data.clientes_prueba} />
        <Stat icon={AlertTriangle} label="Pago vencido" value={data.clientes_vencidos} tone="danger" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Próximos vencimientos (7 días)</h3>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </div>
            {data.proximos_vencimientos.length === 0 && <div className="text-sm text-muted-foreground">Sin vencimientos próximos.</div>}
            <ul className="space-y-2">
              {data.proximos_vencimientos.map((s) => (
                <li key={s.id} className="flex justify-between text-sm border-b border-border last:border-0 py-2">
                  <span className="font-medium">{s.nombre}</span>
                  <span className="text-muted-foreground">{fmtDate(s.fecha_vencimiento)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Últimos pagos</h3>
              <Receipt className="h-4 w-4 text-muted-foreground" />
            </div>
            {data.ultimos_pagos.length === 0 && <div className="text-sm text-muted-foreground">Aún no hay pagos.</div>}
            <ul className="space-y-2">
              {data.ultimos_pagos.map((p) => (
                <li key={p.id} className="flex justify-between text-sm border-b border-border last:border-0 py-2">
                  <span>
                    <span className="font-semibold">{fmtMXN(p.monto)}</span>
                    <span className="text-muted-foreground ml-2">{p.concepto}</span>
                  </span>
                  <span className="text-muted-foreground">{fmtDate(p.created_at)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
